import { Formik, FormikProps } from "formik";
import React, { FC, memo } from "react";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";
import * as Yup from "yup";

import { IEditableSectionRenderProps, useEditableSubmit } from "@/components/EditableSection";
import { Text } from "@/components/Text";
import { MEDIA_SIZE_XS } from "@/core/media";
import { Flex, Group, Label } from "@/eworkitComponents";
import { FormikForm } from "@/formikComponents";
import { FormikRadio } from "@/views/components/Formik";

import { ICompanyFormikProps } from "../types";

export interface IProfilesVisibilityFormValues {
  searchableProfiles: boolean;
}

export const validationProfilesVisibilitySectionSchema = Yup.object();

export const PROFILE_VISIBILITY_MESSENGER_CONTEXT = "profile-visibility-form";

interface IProfilesVisibilityFormProps extends IEditableSectionRenderProps {
  form: FormikProps<IProfilesVisibilityFormValues>;
  onCancel: () => void;
}

export const ProfileVisibilityForm = memo((props: IProfilesVisibilityFormProps) => {
  const { t } = useTranslation("session");

  const isVerySmallScreen = useMedia(MEDIA_SIZE_XS);

  useEditableSubmit(props.form, props.editableProps);

  return (
    <FormikForm>
      <Flex direction="column" innerMargin="md" margin={[isVerySmallScreen ? "xl" : false, false, false, false]}>
        <Flex innerMargin="md" grow={1} direction={isVerySmallScreen ? "column" : "row"}>
          <Flex direction="column">
            <Group label={t("USERS.SETTINGS.CONSULTANT_PROFILES_VISIBILITY.SEARCHABLE.LABEL")} direction="column">
              <Flex align="center" padding={[false, false, "md", false]}>
                <Flex align="center" innerMargin="md">
                  <Flex direction="column" grow={1} innerMargin>
                    <Label>
                      <Flex innerMargin align="center" grow={1}>
                        <FormikRadio
                          value={true}
                          name="searchableProfiles"
                          onChange={() => props.form.setFieldValue("searchableProfiles", true, true)}
                          checked={props.form.values.searchableProfiles}
                          testName={"searchableProfiles-yes"}
                        />
                        <Flex direction="column">
                          <Text size="md" color="black">
                            {t("USERS.SETTINGS.CONSULTANT_PROFILES_VISIBILITY.SEARCHABLE.SEARCHABLE_ON")}
                          </Text>
                          <Text size="md">
                            {t("USERS.SETTINGS.CONSULTANT_PROFILES_VISIBILITY.SEARCHABLE.SEARCHABLE_ON_DESCRIPTION")}
                          </Text>
                        </Flex>
                      </Flex>
                    </Label>
                    <Label>
                      <Flex innerMargin align="flex-start">
                        <FormikRadio
                          value={false}
                          name="searchableProfiles"
                          onChange={() => props.form.setFieldValue("searchableProfiles", false, true)}
                          checked={!props.form.values.searchableProfiles}
                          testName={"searchableProfiles-no"}
                        />
                        <Flex direction="column">
                          <Text size="md" color="black">
                            {t("USERS.SETTINGS.CONSULTANT_PROFILES_VISIBILITY.SEARCHABLE.SEARCHABLE_OFF")}
                          </Text>
                          <Text size="md">
                            {t("USERS.SETTINGS.CONSULTANT_PROFILES_VISIBILITY.SEARCHABLE.SEARCHABLE_OFF_DESCRIPTION")}
                          </Text>
                        </Flex>
                      </Flex>
                    </Label>
                  </Flex>
                </Flex>
              </Flex>
            </Group>
          </Flex>
        </Flex>
      </Flex>
    </FormikForm>
  );
});

interface IProfilesVisibilityFormikProps
  extends IProfilesVisibilityFormProps,
    ICompanyFormikProps<IProfilesVisibilityFormValues> {}

export const ProfilesVisibilityFormik: FC<IProfilesVisibilityFormikProps> = memo(
  ({ initialValues, validationSchema, onSubmit, ...rest }) => (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      render={(form: FormikProps<IProfilesVisibilityFormValues>) => <ProfileVisibilityForm {...rest} form={form} />}
    />
  ),
);
